/**
 * Resolve uma cor CSS para um HEX concreto "#rrggbb".
 *
 * Aceita qualquer forma que o browser entenda: "var(--token)",
 * "color-mix(...)", "rgb(...)", "rgba(...)", nomes ("red") ou hex.
 *
 * Por que existe: as camadas de exportacao (html2canvas, canvas 2D, jsPDF)
 * NAO entendem CSS var() — apenas o browser resolve var() durante o paint do
 * DOM real. Passar "var(--bg)" direto pro html2canvas quebra com
 * 'Attempting to parse an unsupported color function "var"'. Aqui forcamos o
 * browser a resolver, lendo a cor computada de um elemento-sonda; o resultado
 * respeita o tema atual ([data-theme] no <html>).
 */

const HEX_COMPLETO = /^#[0-9a-f]{6}$/i;

function componenteHex(valor) {
  const n = Math.max(0, Math.min(255, Math.round(Number(valor) || 0)));
  return n.toString(16).padStart(2, "0");
}

/**
 * Converte "rgb(r, g, b)" / "rgba(r, g, b, a)" em "#rrggbb" (descarta alpha).
 * Retorna null se nao reconhecer o formato.
 */
function rgbParaHex(corRgb) {
  const match = String(corRgb).match(/rgba?\(([^)]+)\)/i);
  if (!match) return null;
  const partes = match[1]
    .split(/[\s,/]+/)
    .filter(Boolean)
    .map(Number);
  if (partes.length < 3 || partes.slice(0, 3).some(Number.isNaN)) return null;
  return `#${componenteHex(partes[0])}${componenteHex(partes[1])}${componenteHex(
    partes[2]
  )}`;
}

function lerCorComputada(valor) {
  const sonda = document.createElement("span");
  sonda.style.color = valor;
  sonda.style.position = "absolute";
  sonda.style.left = "-9999px";
  sonda.style.opacity = "0";
  sonda.style.pointerEvents = "none";
  document.body.appendChild(sonda);
  try {
    // getComputedStyle resolve var()/color-mix()/nomes para rgb()/rgba().
    return getComputedStyle(sonda).color;
  } finally {
    sonda.remove();
  }
}

/**
 * @param {string} valor      cor CSS (pode conter var(), color-mix(), etc.)
 * @param {string} [fallback] hex usado se a resolucao falhar ou nao houver DOM
 * @returns {string} hex "#rrggbb"
 */
export function resolverCorCss(valor, fallback = "#000000") {
  if (typeof valor !== "string" || !valor.trim()) return fallback;
  const limpo = valor.trim();
  if (HEX_COMPLETO.test(limpo)) return limpo; // ja concreto, nada a resolver
  if (typeof document === "undefined") return fallback; // SSR / sem DOM

  try {
    return rgbParaHex(lerCorComputada(limpo)) || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Resolve uma cor CSS COM alpha e a compõe sobre um fundo hex opaco,
 * retornando hex sólido. Necessário para tokens rgba do tema dark (ex.:
 * --danger-bg: rgba(251,113,133,.14)) chegarem ao jsPDF, que não tem alpha.
 *
 * @param {string} valor     cor CSS (var(), rgba(), hex...)
 * @param {string} fundoHex  hex "#rrggbb" sobre o qual compor
 * @param {string} fallback  hex usado se a resolução falhar
 * @returns {string} hex "#rrggbb" sólido
 */
export function resolverCorCssSobreFundo(valor, fundoHex, fallback) {
  if (typeof valor !== "string" || !valor.trim()) return fallback;
  if (typeof document === "undefined") return fallback;
  try {
    const computado = lerCorComputada(valor.trim());
    const match = String(computado).match(/rgba?\(([^)]+)\)/i);
    if (!match) return fallback;
    const partes = match[1].split(/[\s,/]+/).filter(Boolean).map(Number);
    if (partes.length < 3 || partes.slice(0, 3).some(Number.isNaN)) return fallback;
    const alpha = partes.length >= 4 && !Number.isNaN(partes[3]) ? partes[3] : 1;
    const fundo = String(fundoHex || "#ffffff").replace("#", "");
    const fr = parseInt(fundo.slice(0, 2), 16);
    const fg = parseInt(fundo.slice(2, 4), 16);
    const fb = parseInt(fundo.slice(4, 6), 16);
    const compor = (c, f) => c * alpha + f * (1 - alpha);
    return `#${componenteHex(compor(partes[0], fr))}${componenteHex(
      compor(partes[1], fg)
    )}${componenteHex(compor(partes[2], fb))}`;
  } catch {
    return fallback;
  }
}
